// Centralized site content. Verified company info sourced from alldatatech.ai.
// Placeholder / example content is flagged with `isExample: true`.

export const COMPANY = {
  name: "All Data Tech",
  tagline: "Data. Intelligence. Business Impact.",
  email: "sales.alldatatech@gmail.com",
  address: "Sector-41, Noida, India - 201303",
};

export const NAV_LINKS = [
  { label: "Solutions", to: "/solutions" },
  { label: "Industries", to: "/industries" },
  { label: "Services", to: "/services" },
  { label: "About", to: "/about" },
  { label: "Insights", to: "/insights" },
  { label: "Contact", to: "/contact" },
];

export const CAPABILITIES = [
  "Data Engineering",
  "AI & Machine Learning",
  "Business Intelligence",
  "Predictive Analytics",
  "Cloud Data",
  "Automation",
];

export const IMAGES = {
  heroTeam:
    "https://images.pexels.com/photos/8204363/pexels-photo-8204363.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  aboutTeam:
    "https://images.pexels.com/photos/3865639/pexels-photo-3865639.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
  boardroom:
    "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  ai:
    "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  dataEng:
    "https://images.unsplash.com/photo-1683322499436-f4383dd59f5a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  analytics:
    "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  predictive:
    "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  cloud:
    "https://images.unsplash.com/photo-1667984390538-3dea7a3fe33d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  automation:
    "https://images.unsplash.com/photo-1639322537231-2f206e06af84?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  dashboard2:
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  finance:
    "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  serverRoom:
    "https://images.unsplash.com/photo-1573164713988-8665fc963095?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

export const PROBLEMS = [
  { title: "Fragmented Data", body: "Data is spread across disconnected systems and applications." },
  { title: "Manual Reporting", body: "Teams spend hours collecting, cleaning and consolidating information." },
  { title: "Limited Visibility", body: "Decision-makers lack real-time insight into what is happening across the business." },
  { title: "Operational Inefficiency", body: "Repetitive processes consume valuable employee time." },
  { title: "Unpredictable Outcomes", body: "Organizations struggle to forecast demand, revenue, risk and operational performance." },
];

export const SOLUTIONS = [
  {
    slug: "ai-ml",
    icon: "BrainCircuit",
    number: "01",
    title: "AI & Machine Learning",
    short: "Build intelligent systems that automate decisions, uncover patterns and create new business capabilities.",
    cta: "Explore AI Solutions",
    hero: "Build AI That Works for Your Business.",
    image: "ai",
    intro: "We design and deploy machine learning, generative AI and intelligent automation that turn your data into practical, production-grade capabilities.",
    covers: ["Machine Learning", "Predictive Models", "Generative AI", "LLM applications", "Intelligent automation", "AI-powered analytics", "Custom AI applications"],
    useCases: ["Forecasting", "Classification", "Recommendation systems", "Document intelligence", "Intelligent search", "Workflow automation", "Natural language interfaces", "Predictive maintenance"],
    tech: ["Python", "PyTorch", "LLMs", "Generative AI", "Predictive Modeling"],
  },
  {
    slug: "data-engineering",
    icon: "Database",
    number: "02",
    title: "Data Engineering",
    short: "Design reliable data pipelines and modern data architectures that turn raw information into usable intelligence.",
    cta: "Explore Data Engineering",
    hero: "Build a Data Foundation Ready for What's Next.",
    image: "dataEng",
    intro: "We build the pipelines, warehouses and integrations that give your organization clean, reliable, analytics-ready data.",
    covers: ["Data pipelines", "ETL / ELT", "Data warehouses", "Data lakes", "Data integration", "Data quality", "Data transformation", "Cloud data architecture", "API integrations"],
    useCases: ["Unify data across disconnected systems", "Automate ingestion and transformation", "Establish a single source of truth", "Enable real-time and batch analytics"],
    tech: ["Snowflake", "Databricks", "BigQuery", "SQL", "Python", "APIs"],
  },
  {
    slug: "analytics-bi",
    icon: "BarChart3",
    number: "03",
    title: "Analytics & Business Intelligence",
    short: "Transform complex data into dashboards, KPIs and insights that help leaders make better decisions.",
    cta: "Explore Analytics",
    hero: "See What Your Business Data Is Telling You.",
    image: "analytics",
    intro: "We turn raw metrics into clear, decision-ready dashboards and reporting so leaders can act with confidence.",
    covers: ["Executive dashboards", "KPI dashboards", "Operational analytics", "Financial analytics", "Sales analytics", "Customer analytics", "Real-time reporting", "Self-service BI"],
    useCases: ["Consolidate reporting into one view", "Track KPIs in real time", "Enable self-service exploration", "Standardize metrics across teams"],
    tech: ["Power BI", "Tableau", "SQL", "Modern BI platforms"],
  },
  {
    slug: "predictive-analytics",
    icon: "TrendingUp",
    number: "04",
    title: "Predictive Analytics",
    short: "Use statistical models and machine learning to forecast demand, identify risks and anticipate future outcomes.",
    cta: "Explore Predictive Analytics",
    hero: "Anticipate What Comes Next.",
    image: "predictive",
    intro: "We combine historical data, statistical modeling and machine learning to help you forecast and plan with confidence.",
    covers: ["Demand forecasting", "Risk management", "Sales forecasting", "Customer behavior prediction", "Scenario modeling"],
    useCases: ["Forecast demand and revenue", "Identify and manage risk", "Anticipate customer behavior", "Plan capacity and operations"],
    tech: ["Python", "Machine Learning", "Statistical Modeling"],
  },
  {
    slug: "cloud-data",
    icon: "Cloud",
    number: "05",
    title: "Cloud Data Solutions",
    short: "Build scalable cloud-based data environments using modern infrastructure and architecture.",
    cta: "Explore Cloud Solutions",
    hero: "Modernize Your Data Infrastructure.",
    image: "cloud",
    intro: "We design secure, scalable cloud data platforms that are ready for analytics, AI and continuous growth.",
    covers: ["Cloud data architecture", "Scalability & elasticity", "Data integration", "Security & governance", "Analytics readiness", "Cost optimization"],
    useCases: ["Migrate legacy data to the cloud", "Scale infrastructure with demand", "Centralize governance and security", "Reduce infrastructure cost"],
    tech: ["AWS", "Microsoft Azure", "Google Cloud", "Snowflake", "Databricks"],
  },
  {
    slug: "automation",
    icon: "Workflow",
    number: "06",
    title: "Data Automation",
    short: "Eliminate repetitive data processes through intelligent automation and integrated workflows.",
    cta: "Explore Automation",
    hero: "Automate the Routine.",
    image: "automation",
    intro: "We remove manual, repetitive data work with reliable automated workflows and integrations.",
    covers: ["Workflow automation", "Automated reporting", "Data integration", "Process orchestration", "Smart automation"],
    useCases: ["Automate manual reporting", "Orchestrate multi-step workflows", "Integrate systems end to end", "Reduce operational overhead"],
    tech: ["Python", "APIs", "Cloud services"],
  },
];

export const SOLUTION_DETAIL_SLUGS = ["ai-ml", "data-engineering", "analytics-bi", "cloud-data"];

export const PIPELINE_STAGES = [
  { key: "sources", label: "Data Sources", desc: "Apps, databases, APIs, files, events" },
  { key: "engineering", label: "Data Engineering", desc: "Pipelines, ETL/ELT, quality" },
  { key: "analytics", label: "Analytics", desc: "Dashboards, KPIs, reporting" },
  { key: "ai", label: "AI / Machine Learning", desc: "Models, prediction, generative AI" },
  { key: "intelligence", label: "Intelligence", desc: "Insight, foresight, clarity" },
  { key: "action", label: "Business Action", desc: "Decisions, automation, impact" },
];

export const INDUSTRIES = [
  {
    icon: "Landmark",
    title: "Financial Services",
    image: "finance",
    tagline: "Risk, fraud and regulatory-grade data platforms.",
    challenge: "Fragmented risk data and slow, manual regulatory reporting limit confident decision-making.",
    solution: "Unified data platforms with automated pipelines, risk models and audit-ready analytics.",
    useCases: ["Risk modeling", "Fraud detection", "Regulatory reporting", "Customer analytics"],
  },
  {
    icon: "HeartPulse",
    title: "Healthcare",
    image: "aboutTeam",
    tagline: "Secure analytics for clinical and operational data.",
    challenge: "Sensitive data lives in silos, making operational and clinical insight hard to surface securely.",
    solution: "Governed data foundations and analytics that respect privacy while unlocking insight.",
    useCases: ["Operational analytics", "Patient insights", "Capacity forecasting", "Quality reporting"],
  },
  {
    icon: "ShoppingCart",
    title: "Retail & E-commerce",
    image: "analytics",
    tagline: "Demand, pricing and customer intelligence.",
    challenge: "Disconnected sales, inventory and marketing data obscure a true view of the customer.",
    solution: "A single source of truth powering demand forecasting, personalization and pricing.",
    useCases: ["Demand forecasting", "Recommendations", "Customer analytics", "Inventory optimization"],
  },
  {
    icon: "Factory",
    title: "Manufacturing",
    image: "dataEng",
    tagline: "Predictive operations and quality intelligence.",
    challenge: "Limited visibility into equipment, quality and demand drives costly reactive decisions.",
    solution: "Predictive analytics and connected operational data for proactive planning.",
    useCases: ["Predictive maintenance", "Quality analytics", "Demand planning", "OEE dashboards"],
  },
  {
    icon: "Truck",
    title: "Logistics & Supply Chain",
    image: "serverRoom",
    tagline: "End-to-end visibility and optimization.",
    challenge: "Supply chain data is scattered across partners, limiting real-time visibility.",
    solution: "Integrated data and optimization models for resilient, efficient operations.",
    useCases: ["Route optimization", "Inventory forecasting", "Supply visibility", "Cost analytics"],
  },
  {
    icon: "Cpu",
    title: "Technology",
    image: "ai",
    tagline: "Product, usage and growth analytics at scale.",
    challenge: "High-volume product and usage data is hard to turn into actionable growth insight.",
    solution: "Scalable analytics and ML that power product, growth and retention decisions.",
    useCases: ["Product analytics", "Usage telemetry", "Churn prediction", "Growth analytics"],
  },
];

export const OUTCOMES = [
  { icon: "Target", title: "Make Better Decisions", desc: "Give leadership reliable, actionable intelligence." },
  { icon: "Zap", title: "Automate the Routine", desc: "Reduce manual processes and operational overhead." },
  { icon: "LineChart", title: "Predict What Comes Next", desc: "Use data and AI to anticipate business outcomes." },
  { icon: "Rocket", title: "Scale With Confidence", desc: "Build data infrastructure that grows with the organization." },
];

export const TECH_ECOSYSTEM = [
  { category: "Cloud", items: ["AWS", "Microsoft Azure", "Google Cloud"] },
  { category: "Data Platforms", items: ["Snowflake", "Databricks", "BigQuery", "SQL platforms"] },
  { category: "Analytics", items: ["Power BI", "Tableau", "Modern BI"] },
  { category: "Development", items: ["Python", "SQL", "APIs", "Modern app tech"] },
  { category: "AI", items: ["Machine Learning", "Generative AI", "LLMs", "Predictive Modeling"] },
];

export const PROCESS = [
  { no: "01", title: "Discover", desc: "Understand the business problem, existing data environment and desired outcomes." },
  { no: "02", title: "Architect", desc: "Design the right data, AI and technology architecture." },
  { no: "03", title: "Build", desc: "Develop the pipelines, models, dashboards, integrations and applications." },
  { no: "04", title: "Deploy", desc: "Move the solution into production with reliability and scalability in mind." },
  { no: "05", title: "Optimize", desc: "Continuously improve performance, automation and business value." },
];

export const CASE_STUDIES = [
  {
    isExample: true,
    title: "Predictive Analytics Platform",
    industry: "Manufacturing",
    image: "predictive",
    challenge: "Limited visibility into future operational demand.",
    approach: "Assessed historical operational data and designed a forecasting architecture aligned to planning cycles.",
    solution: "A predictive analytics platform combining historical data, machine learning and interactive dashboards.",
    tech: ["Python", "Machine Learning", "Power BI"],
    outcome: "Improved forecasting visibility and faster decision-making.",
  },
  {
    isExample: true,
    title: "Unified Cloud Data Warehouse",
    industry: "Retail & E-commerce",
    image: "analytics",
    challenge: "Sales, inventory and marketing data lived in disconnected systems.",
    approach: "Mapped source systems and designed automated ELT pipelines into a governed cloud warehouse.",
    solution: "A modern cloud data warehouse with automated pipelines and a single source of truth.",
    tech: ["Snowflake", "ETL/ELT", "SQL"],
    outcome: "Consolidated reporting and reduced manual data preparation.",
  },
  {
    isExample: true,
    title: "Document Intelligence with GenAI",
    industry: "Professional Services",
    image: "ai",
    challenge: "High volume of unstructured documents required manual review.",
    approach: "Built an LLM-based extraction and search workflow with human-in-the-loop validation.",
    solution: "A generative-AI document intelligence workflow with intelligent search and extraction.",
    tech: ["LLMs", "Python", "APIs"],
    outcome: "Faster document processing and improved information retrieval.",
  },
];

export const WHY = [
  { icon: "Focus", title: "Business First", desc: "We begin with the business problem, not the technology." },
  { icon: "PenTool", title: "Custom by Design", desc: "Solutions are designed around your specific environment and objectives." },
  { icon: "Boxes", title: "Modern Architecture", desc: "Build scalable, maintainable systems using modern data and cloud technologies." },
  { icon: "Sparkles", title: "AI Ready", desc: "Create the data foundation required for practical AI adoption." },
  { icon: "Handshake", title: "Long-Term Partnership", desc: "Support continuous improvement rather than treating technology as a one-time project." },
];

export const SERVICES = [
  { title: "Data Engineering", desc: "Reliable pipelines and modern data architecture.", slug: "data-engineering" },
  { title: "AI & Machine Learning", desc: "Intelligent systems, models and automation.", slug: "ai-ml" },
  { title: "Business Intelligence", desc: "Dashboards, KPIs and decision-ready reporting.", slug: "analytics-bi" },
  { title: "Data Analytics", desc: "Turn complex data into clear insight.", slug: "analytics-bi" },
  { title: "Predictive Analytics", desc: "Forecast demand, risk and outcomes.", slug: "predictive-analytics" },
  { title: "Cloud Data Solutions", desc: "Scalable, secure cloud data platforms.", slug: "cloud-data" },
  { title: "Data Automation", desc: "Automate repetitive data processes.", slug: "automation" },
  { title: "Custom Data Solutions", desc: "Tailored data and AI for your domain.", slug: null },
];

export const INSIGHT_CATEGORIES = [
  "All",
  "AI & Machine Learning",
  "Data Engineering",
  "Analytics",
  "Business Intelligence",
  "Cloud",
  "Data Strategy",
];

export const ARTICLES = [
  {
    slug: "how-ai-is-changing-the-way-businesses-use-data",
    category: "AI & Machine Learning",
    title: "How AI Is Changing the Way Businesses Use Data",
    subtitle: "AI is shifting data from a record of the past into an engine for foresight and action.",
    excerpt: "AI is shifting data from a record of the past into an engine for foresight, automation and competitive advantage.",
    image: "ai",
    date: "June 2026",
    readTime: "6 min read",
    featured: true,
    isExample: true,
    body: [
      { type: "p", text: "For years, most organizations used data to describe what had already happened. Reports summarized last quarter; dashboards showed yesterday's performance. That view is valuable — but it is fundamentally backward-looking." },
      { type: "h2", text: "From describing the past to shaping the future" },
      { type: "p", text: "Machine learning changes the question. Instead of only asking \"what happened?\", teams can ask \"what is likely to happen next, and what should we do about it?\" Forecasting, classification and recommendation models turn historical patterns into forward-looking guidance." },
      { type: "quote", text: "AI projects don't begin with models. They begin with reliable data." },
      { type: "h2", text: "Why the foundation matters" },
      { type: "p", text: "The organizations getting real value from AI are rarely the ones with the most sophisticated models. They are the ones with clean, well-governed, accessible data. Without that foundation, even the best model produces unreliable results." },
      { type: "p", text: "The practical path forward is unglamorous but effective: invest in data engineering, establish a single source of truth, then layer analytics and AI on top. Intelligence compounds on a solid base." },
    ],
  },
  {
    slug: "building-a-modern-data-foundation-for-the-ai-era",
    category: "Data Engineering",
    title: "Building a Modern Data Foundation for the AI Era",
    subtitle: "Reliable pipelines and a single source of truth are the prerequisites for practical AI.",
    excerpt: "Before models and dashboards, there is data engineering. Here's what a modern foundation looks like.",
    image: "dataEng",
    date: "June 2026",
    readTime: "5 min read",
    featured: false,
    isExample: true,
    body: [
      { type: "p", text: "Every analytics or AI initiative depends on one thing: trustworthy data, available where and when it's needed. Building that foundation is the work of data engineering." },
      { type: "h2", text: "Pipelines over point solutions" },
      { type: "p", text: "Modern data engineering favors automated, observable pipelines over brittle manual processes. ETL and ELT patterns move data reliably from source systems into governed warehouses and lakes." },
      { type: "quote", text: "A single source of truth is worth more than a dozen dashboards built on inconsistent data." },
      { type: "h2", text: "Quality is a feature" },
      { type: "p", text: "Data quality, lineage and governance are not afterthoughts — they are the difference between insight and confusion. Investing early pays dividends across every downstream use case." },
    ],
  },
  {
    slug: "from-dashboards-to-decisions",
    category: "Business Intelligence",
    title: "From Dashboards to Decisions: The Evolution of Business Intelligence",
    subtitle: "The best BI isn't measured in charts — it's measured in decisions made with confidence.",
    excerpt: "Great business intelligence is designed around decisions, not vanity metrics.",
    image: "analytics",
    date: "May 2026",
    readTime: "4 min read",
    featured: false,
    isExample: true,
    body: [
      { type: "p", text: "A dashboard full of metrics is easy to build and easy to ignore. The value of business intelligence comes from aligning what you measure with the decisions leaders actually make." },
      { type: "h2", text: "Design around the decision" },
      { type: "p", text: "Start from the question a leader is trying to answer, then work backward to the metrics and data needed. This produces focused, high-signal dashboards people return to." },
      { type: "quote", text: "If a dashboard doesn't change a decision, it's decoration." },
    ],
  },
  {
    slug: "turning-historical-data-into-future-insights",
    category: "Analytics",
    title: "Turning Historical Data Into Future Insights",
    subtitle: "Predictive analytics gives planning teams a forward view grounded in real history.",
    excerpt: "How forecasting models turn the data you already have into decisions about what's next.",
    image: "predictive",
    date: "May 2026",
    readTime: "5 min read",
    featured: false,
    isExample: true,
    body: [
      { type: "p", text: "Most businesses are sitting on years of operational history. Predictive analytics is the discipline of turning that history into reliable expectations about the future." },
      { type: "h2", text: "Forecasting as a planning tool" },
      { type: "p", text: "Demand, revenue, risk and capacity can all be forecast with the right combination of statistical modeling and machine learning — giving planning teams a defensible starting point." },
      { type: "quote", text: "The goal isn't a perfect prediction. It's a better decision than a guess." },
    ],
  },
  {
    slug: "why-cloud-data-platforms-matter",
    category: "Cloud",
    title: "Why Cloud Data Platforms Matter for Modern Businesses",
    subtitle: "Scalability, integration and analytics-readiness are now table stakes.",
    excerpt: "Cloud data platforms provide the elasticity and integration modern analytics and AI demand.",
    image: "cloud",
    date: "April 2026",
    readTime: "7 min read",
    featured: false,
    isExample: true,
    body: [
      { type: "p", text: "Legacy, on-premise data environments struggle with the scale and flexibility modern analytics and AI require. Cloud data platforms change the economics and the possibilities." },
      { type: "h2", text: "Elastic by design" },
      { type: "p", text: "Cloud platforms scale compute and storage independently and on demand, so you pay for what you use and never hit a hard ceiling during peak workloads." },
      { type: "quote", text: "Modernization is less about moving servers and more about unlocking what your data can do." },
    ],
  },
  {
    slug: "building-a-data-strategy-that-scales",
    category: "Data Strategy",
    title: "Building a Data Strategy That Scales With Your Business",
    subtitle: "A strategy connects data investments to business outcomes — not the other way around.",
    excerpt: "A pragmatic framework for a data strategy that grows with your organization.",
    image: "boardroom",
    date: "April 2026",
    readTime: "6 min read",
    featured: false,
    isExample: true,
    body: [
      { type: "p", text: "Technology decisions made without a strategy tend to accumulate into complexity. A good data strategy keeps every investment tied to a business outcome." },
      { type: "h2", text: "Start with outcomes" },
      { type: "p", text: "Define the decisions and outcomes that matter most, then design the data capabilities required to support them. This keeps scope honest and value visible." },
      { type: "quote", text: "Strategy is choosing what not to build as much as what to build." },
    ],
  },
];
